import type { TProject, TProjectMetadata, TBackground } from "@/project/types";
import { getProjectDurationFromScenes } from "@/timeline/scenes";
import type { MediaAsset } from "@/media/types";
import type { Mask } from "@/masks/types";
import { IndexedDBAdapter } from "./indexeddb-adapter";
import { OPFSAdapter } from "./opfs-adapter";
import {
	type StorageCapacityCheckResult,
	StorageQuotaExceededError,
	evaluateStorageCapacity,
	isStorageQuotaExceededError,
	readStorageQuotaStatus,
} from "./quota";
import type {
	MediaAssetData,
	StorageConfig,
	SerializedProject,
	SerializedScene,
	CustomBackgroundMetadata,
	UserSoundMetadata,
	BannerMetadata,
	BannerTransform,
	LookPresetMetadata,
} from "./types";
import type { SavedSoundsData, SavedSound, SoundEffect } from "@/sounds/types";
import {
	migrations,
	runStorageMigrations,
} from "@/services/storage/migrations";
import type { Bookmark, SceneTracks, TScene } from "@/timeline";
import { roundMediaTime } from "@/wasm";
import { generateUUID } from "@/utils/id";

function normalizeBookmarks({ raw }: { raw: unknown }): Bookmark[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item): Bookmark | null => {
			if (typeof item === "number") {
				return { time: roundMediaTime({ time: item }) };
			}
			const obj = item as Record<string, unknown>;
			if (
				typeof obj !== "object" ||
				obj === null ||
				typeof obj.time !== "number"
			) {
				return null;
			}
			return {
				time: roundMediaTime({ time: obj.time }),
				...(typeof obj.note === "string" && { note: obj.note }),
				...(typeof obj.color === "string" && { color: obj.color }),
				...(typeof obj.duration === "number" && {
					duration: roundMediaTime({ time: obj.duration }),
				}),
			};
		})
		.filter((b): b is Bookmark => b !== null);
}

class StorageService {
	private projectsAdapter: IndexedDBAdapter<SerializedProject>;
	private savedSoundsAdapter: IndexedDBAdapter<SavedSoundsData>;
	// Global (not per-project) — mirrors the per-project media split (getProjectMediaAdapters
	// below) but with no projectId suffix, so a background uploaded once is one single record
	// reused by every project instead of copied into each project's own storage.
	private customBackgroundsAdapter: IndexedDBAdapter<CustomBackgroundMetadata>;
	private customBackgroundFilesAdapter: OPFSAdapter;
	// Global (not per-project) — mirrors customBackgroundsAdapter/customBackgroundFilesAdapter
	// above, for the user's personal "Мои звуки" library instead of backgrounds.
	private userSoundsAdapter: IndexedDBAdapter<UserSoundMetadata>;
	private userSoundFilesAdapter: OPFSAdapter;
	// Global (not per-project) — mirrors userSoundsAdapter/userSoundFilesAdapter above, for the
	// "Баннеры" library instead of sounds.
	private bannersAdapter: IndexedDBAdapter<BannerMetadata>;
	private bannerFilesAdapter: OPFSAdapter;
	// Global (not per-project) — mirrors bannersAdapter/bannerFilesAdapter above, for the "look"
	// presets library (presets/) instead of banners. presetLoopFilesAdapter only ever has an entry
	// for presets that actually saved a loop file — see LookPresetMetadata's own doc comment.
	private presetsAdapter: IndexedDBAdapter<LookPresetMetadata>;
	private presetLoopFilesAdapter: OPFSAdapter;
	private config: StorageConfig;
	private migrationsPromise: Promise<void> | null = null;

	constructor() {
		this.config = {
			projectsDb: "video-editor-projects",
			mediaDb: "video-editor-media",
			savedSoundsDb: "video-editor-saved-sounds",
			customBackgroundsDb: "video-editor-custom-backgrounds",
			userSoundsDb: "video-editor-user-sounds",
			bannersDb: "video-editor-banners",
			presetsDb: "video-editor-presets",
			version: 1,
		};

		this.projectsAdapter = new IndexedDBAdapter<SerializedProject>({
			dbName: this.config.projectsDb,
			storeName: "projects",
			version: this.config.version,
		});

		this.savedSoundsAdapter = new IndexedDBAdapter<SavedSoundsData>({
			dbName: this.config.savedSoundsDb,
			storeName: "saved-sounds",
			version: this.config.version,
		});

		this.customBackgroundsAdapter = new IndexedDBAdapter<CustomBackgroundMetadata>({
			dbName: this.config.customBackgroundsDb,
			storeName: "custom-backgrounds",
			version: this.config.version,
		});
		this.customBackgroundFilesAdapter = new OPFSAdapter("custom-backgrounds");

		this.userSoundsAdapter = new IndexedDBAdapter<UserSoundMetadata>({
			dbName: this.config.userSoundsDb,
			storeName: "user-sounds",
			version: this.config.version,
		});
		this.userSoundFilesAdapter = new OPFSAdapter("user-sounds");

		this.bannersAdapter = new IndexedDBAdapter<BannerMetadata>({
			dbName: this.config.bannersDb,
			storeName: "banners",
			version: this.config.version,
		});
		this.bannerFilesAdapter = new OPFSAdapter("banners");

		this.presetsAdapter = new IndexedDBAdapter<LookPresetMetadata>({
			dbName: this.config.presetsDb,
			storeName: "presets",
			version: this.config.version,
		});
		this.presetLoopFilesAdapter = new OPFSAdapter("preset-loops");
	}

	private async ensureMigrations(): Promise<void> {
		if (this.migrationsPromise) {
			await this.migrationsPromise;
			return;
		}

		this.migrationsPromise = runStorageMigrations({ migrations }).then(
			() => undefined,
		);
		await this.migrationsPromise;
	}

	private getProjectMediaAdapters({ projectId }: { projectId: string }) {
		const mediaMetadataAdapter = new IndexedDBAdapter<MediaAssetData>({
			dbName: `${this.config.mediaDb}-${projectId}`,
			storeName: "media-metadata",
			version: this.config.version,
		});

		const mediaAssetsAdapter = new OPFSAdapter(`media-files-${projectId}`);

		return { mediaMetadataAdapter, mediaAssetsAdapter };
	}

	async canStoreFile({
		size,
	}: {
		size: number;
	}): Promise<StorageCapacityCheckResult> {
		const quotaStatus = await readStorageQuotaStatus();
		return evaluateStorageCapacity({
			requiredBytes: size,
			quotaStatus,
		});
	}

	isQuotaExceededError({ error }: { error: unknown }): boolean {
		return isStorageQuotaExceededError({ error });
	}

	private stripAudioBuffers({ tracks }: { tracks: SceneTracks }): SceneTracks {
		return {
			...tracks,
			audio: tracks.audio.map((track) => ({
				...track,
				elements: track.elements.map((element) => {
					const { buffer: _buffer, ...rest } = element;
					return rest;
				}),
			})),
		};
	}

	async saveProject({ project }: { project: TProject }): Promise<void> {
		const duration =
			project.metadata.duration ??
			getProjectDurationFromScenes({ scenes: project.scenes });
		const serializedScenes: SerializedScene[] = project.scenes.map((scene) => ({
			id: scene.id,
			name: scene.name,
			isMain: scene.isMain,
			tracks: this.stripAudioBuffers({ tracks: scene.tracks }),
			bookmarks: scene.bookmarks,
			createdAt: scene.createdAt.toISOString(),
			updatedAt: scene.updatedAt.toISOString(),
		}));

		const serializedProject: SerializedProject = {
			metadata: {
				id: project.metadata.id,
				name: project.metadata.name,
				thumbnail: project.metadata.thumbnail,
				duration,
				createdAt: project.metadata.createdAt.toISOString(),
				updatedAt: project.metadata.updatedAt.toISOString(),
			},
			scenes: serializedScenes,
			currentSceneId: project.currentSceneId,
			settings: project.settings,
			version: project.version,
			timelineViewState: project.timelineViewState,
		};

		await this.projectsAdapter.set({
			key: project.metadata.id,
			value: serializedProject,
		});
	}

	async loadProject({
		id,
	}: {
		id: string;
	}): Promise<{ project: TProject } | null> {
		await this.ensureMigrations();
		const serializedProject = await this.projectsAdapter.get(id);

		if (!serializedProject) return null;

		if (
			typeof serializedProject !== "object" ||
			serializedProject === null ||
			typeof serializedProject.metadata !== "object" ||
			serializedProject.metadata === null
		) {
			console.warn(
				"[storage] Skipping malformed project entry (missing metadata):",
				{ id, entry: serializedProject },
			);
			return null;
		}

		const scenes =
			serializedProject.scenes?.map((scene) => ({
				id: scene.id,
				name: scene.name,
				isMain: scene.isMain,
				tracks: scene.tracks,
				bookmarks: normalizeBookmarks({ raw: scene.bookmarks }),
				createdAt: new Date(scene.createdAt),
				updatedAt: new Date(scene.updatedAt),
			})) ?? [];

		const project: TProject = {
			metadata: {
				id: serializedProject.metadata.id,
				name: serializedProject.metadata.name,
				thumbnail: serializedProject.metadata.thumbnail,
				duration: roundMediaTime({
					time:
						serializedProject.metadata.duration ??
						getProjectDurationFromScenes({ scenes }),
				}),
				createdAt: new Date(serializedProject.metadata.createdAt),
				updatedAt: new Date(serializedProject.metadata.updatedAt),
			},
			scenes,
			currentSceneId: serializedProject.currentSceneId || "",
			settings: serializedProject.settings,
			version: serializedProject.version,
			timelineViewState: serializedProject.timelineViewState,
		};

		return { project };
	}

	async loadAllProjects(): Promise<TProject[]> {
		const projectIds = await this.projectsAdapter.list();
		const projects: TProject[] = [];

		for (const id of projectIds) {
			const result = await this.loadProject({ id });
			if (result?.project) {
				projects.push(result.project);
			}
		}

		return projects.sort(
			(a, b) => b.metadata.updatedAt.getTime() - a.metadata.updatedAt.getTime(),
		);
	}

	async loadAllProjectsMetadata(): Promise<TProjectMetadata[]> {
		await this.ensureMigrations();
		const serializedProjects = await this.projectsAdapter.getAll();

		const metadata: TProjectMetadata[] = [];
		for (const serializedProject of serializedProjects) {
			if (
				typeof serializedProject !== "object" ||
				serializedProject === null ||
				typeof serializedProject.metadata !== "object" ||
				serializedProject.metadata === null
			) {
				console.warn(
					"[storage] Skipping malformed project entry (missing metadata):",
					serializedProject,
				);
				continue;
			}

			metadata.push({
				id: serializedProject.metadata.id,
				name: serializedProject.metadata.name,
				thumbnail: serializedProject.metadata.thumbnail,
				duration: roundMediaTime({
					time:
						serializedProject.metadata.duration ??
						getProjectDurationFromScenes({
							scenes: (serializedProject.scenes ?? []) as unknown as TScene[],
						}),
				}),
				createdAt: new Date(serializedProject.metadata.createdAt),
				updatedAt: new Date(serializedProject.metadata.updatedAt),
			});
		}

		return metadata.sort(
			(a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
		);
	}

	async deleteProject({ id }: { id: string }): Promise<void> {
		await this.projectsAdapter.remove(id);
	}

	async saveMediaAsset({
		projectId,
		mediaAsset,
	}: {
		projectId: string;
		mediaAsset: MediaAsset;
	}): Promise<void> {
		const { mediaMetadataAdapter, mediaAssetsAdapter } =
			this.getProjectMediaAdapters({ projectId });

		const metadata: MediaAssetData = {
			id: mediaAsset.id,
			name: mediaAsset.name,
			type: mediaAsset.type,
			size: mediaAsset.file.size,
			lastModified: mediaAsset.file.lastModified,
			width: mediaAsset.width,
			height: mediaAsset.height,
			duration: mediaAsset.duration,
			thumbnailUrl: mediaAsset.thumbnailUrl,
			ephemeral: mediaAsset.ephemeral,
		};

		try {
			await mediaAssetsAdapter.set({
				key: mediaAsset.id,
				value: mediaAsset.file,
			});
			await mediaMetadataAdapter.set({
				key: mediaAsset.id,
				value: metadata,
			});
		} catch (error) {
			try {
				await mediaAssetsAdapter.remove(mediaAsset.id);
			} catch {
				// Ignore cleanup failures so the original storage error is preserved.
			}

			if (this.isQuotaExceededError({ error })) {
				throw new StorageQuotaExceededError({
					requiredBytes: mediaAsset.file.size,
				});
			}

			throw error;
		}
	}

	async loadMediaAsset({
		projectId,
		id,
	}: {
		projectId: string;
		id: string;
	}): Promise<MediaAsset | null> {
		const { mediaMetadataAdapter, mediaAssetsAdapter } =
			this.getProjectMediaAdapters({ projectId });

		const [file, metadata] = await Promise.all([
			mediaAssetsAdapter.get(id),
			mediaMetadataAdapter.get(id),
		]);

		if (!file || !metadata) return null;

		let url: string;
		if (metadata.type === "image" && (!file.type || file.type === "")) {
			try {
				const text = await file.text();
				if (text.trim().startsWith("<svg")) {
					const svgBlob = new Blob([text], { type: "image/svg+xml" });
					url = URL.createObjectURL(svgBlob);
				} else {
					url = URL.createObjectURL(file);
				}
			} catch {
				url = URL.createObjectURL(file);
			}
		} else {
			url = URL.createObjectURL(file);
		}

		return {
			id: metadata.id,
			name: metadata.name,
			type: metadata.type,
			file,
			url,
			width: metadata.width,
			height: metadata.height,
			duration: metadata.duration,
			thumbnailUrl: metadata.thumbnailUrl,
			ephemeral: metadata.ephemeral,
		};
	}

	async loadAllMediaAssets({
		projectId,
	}: {
		projectId: string;
	}): Promise<MediaAsset[]> {
		const { mediaMetadataAdapter } = this.getProjectMediaAdapters({
			projectId,
		});

		const mediaIds = await mediaMetadataAdapter.list();
		const mediaItems: MediaAsset[] = [];

		for (const id of mediaIds) {
			const item = await this.loadMediaAsset({ projectId, id });
			if (item) {
				mediaItems.push(item);
			}
		}

		return mediaItems;
	}

	async deleteMediaAsset({
		projectId,
		id,
	}: {
		projectId: string;
		id: string;
	}): Promise<void> {
		const { mediaMetadataAdapter, mediaAssetsAdapter } =
			this.getProjectMediaAdapters({ projectId });

		await Promise.all([
			mediaAssetsAdapter.remove(id),
			mediaMetadataAdapter.remove(id),
		]);
	}

	async deleteProjectMedia({
		projectId,
	}: {
		projectId: string;
	}): Promise<void> {
		const { mediaMetadataAdapter, mediaAssetsAdapter } =
			this.getProjectMediaAdapters({ projectId });

		await Promise.all([
			mediaMetadataAdapter.clear(),
			mediaAssetsAdapter.clear(),
		]);
	}

	async clearAllData(): Promise<void> {
		await this.projectsAdapter.clear();
		// project-specific media and timelines cleaned up when projects are deleted
	}

	async getStorageInfo(): Promise<{
		projects: number;
		isOPFSSupported: boolean;
		isIndexedDBSupported: boolean;
	}> {
		const projectIds = await this.projectsAdapter.list();

		return {
			projects: projectIds.length,
			isOPFSSupported: this.isOPFSSupported(),
			isIndexedDBSupported: this.isIndexedDBSupported(),
		};
	}

	async getProjectStorageInfo({ projectId }: { projectId: string }): Promise<{
		mediaItems: number;
	}> {
		const { mediaMetadataAdapter } = this.getProjectMediaAdapters({
			projectId,
		});

		const mediaIds = await mediaMetadataAdapter.list();

		return {
			mediaItems: mediaIds.length,
		};
	}

	async saveCustomBackground({
		file,
		name,
	}: {
		file: File;
		name: string;
	}): Promise<CustomBackgroundMetadata> {
		const metadata: CustomBackgroundMetadata = {
			id: generateUUID(),
			name,
			createdAt: new Date().toISOString(),
		};

		await this.customBackgroundFilesAdapter.set({ key: metadata.id, value: file });
		await this.customBackgroundsAdapter.set({ key: metadata.id, value: metadata });

		return metadata;
	}

	async loadCustomBackgrounds(): Promise<
		Array<{ metadata: CustomBackgroundMetadata; file: File }>
	> {
		const ids = await this.customBackgroundsAdapter.list();
		const items: Array<{ metadata: CustomBackgroundMetadata; file: File }> = [];

		for (const id of ids) {
			const [metadata, file] = await Promise.all([
				this.customBackgroundsAdapter.get(id),
				this.customBackgroundFilesAdapter.get(id),
			]);
			if (metadata && file) {
				items.push({ metadata, file });
			}
		}

		return items;
	}

	async removeCustomBackground({ id }: { id: string }): Promise<void> {
		await Promise.all([
			this.customBackgroundsAdapter.remove(id),
			this.customBackgroundFilesAdapter.remove(id),
		]);
	}

	async saveUserSound({
		file,
		name,
		duration,
	}: {
		file: File;
		name: string;
		duration?: number;
	}): Promise<UserSoundMetadata> {
		const metadata: UserSoundMetadata = {
			id: generateUUID(),
			name,
			type: file.type,
			size: file.size,
			duration,
			createdAt: new Date().toISOString(),
		};

		try {
			await this.userSoundFilesAdapter.set({ key: metadata.id, value: file });
			await this.userSoundsAdapter.set({ key: metadata.id, value: metadata });
		} catch (error) {
			try {
				await this.userSoundFilesAdapter.remove(metadata.id);
			} catch {
				// Ignore cleanup failures so the original storage error is preserved.
			}

			if (this.isQuotaExceededError({ error })) {
				throw new StorageQuotaExceededError({ requiredBytes: file.size });
			}

			throw error;
		}

		return metadata;
	}

	async loadUserSounds(): Promise<
		Array<{ metadata: UserSoundMetadata; file: File }>
	> {
		const ids = await this.userSoundsAdapter.list();
		const items: Array<{ metadata: UserSoundMetadata; file: File }> = [];

		for (const id of ids) {
			const [metadata, file] = await Promise.all([
				this.userSoundsAdapter.get(id),
				this.userSoundFilesAdapter.get(id),
			]);
			if (metadata && file) {
				items.push({ metadata, file });
			}
		}

		return items;
	}

	async loadUserSound({
		id,
	}: {
		id: string;
	}): Promise<{ metadata: UserSoundMetadata; file: File } | null> {
		const [metadata, file] = await Promise.all([
			this.userSoundsAdapter.get(id),
			this.userSoundFilesAdapter.get(id),
		]);
		if (!metadata || !file) return null;
		return { metadata, file };
	}

	async removeUserSound({ id }: { id: string }): Promise<void> {
		await Promise.all([
			this.userSoundsAdapter.remove(id),
			this.userSoundFilesAdapter.remove(id),
		]);
	}

	/** Writes back the result of a copyright check (see sounds/user-sounds-store.ts's
	 * checkCopyright) onto an already-saved sound's metadata — the only UserSoundMetadata field
	 * ever updated in place after saveUserSound. Returns null if the sound was removed in the
	 * meantime (e.g. the check was still in flight when the user deleted it). */
	async updateUserSoundCopyrightCheck({
		id,
		copyrightCheck,
	}: {
		id: string;
		copyrightCheck: UserSoundMetadata["copyrightCheck"];
	}): Promise<UserSoundMetadata | null> {
		const existing = await this.userSoundsAdapter.get(id);
		if (!existing) return null;

		const metadata: UserSoundMetadata = { ...existing, copyrightCheck };
		await this.userSoundsAdapter.set({ key: id, value: metadata });
		return metadata;
	}

	async saveBanner({
		file,
		name,
		mediaType,
		width,
		height,
		duration,
	}: {
		file: File;
		name: string;
		mediaType: "image" | "video";
		width?: number;
		height?: number;
		duration?: number;
	}): Promise<BannerMetadata> {
		const metadata: BannerMetadata = {
			id: generateUUID(),
			name,
			mediaType,
			type: file.type,
			size: file.size,
			width,
			height,
			duration,
			createdAt: new Date().toISOString(),
		};

		try {
			await this.bannerFilesAdapter.set({ key: metadata.id, value: file });
			await this.bannersAdapter.set({ key: metadata.id, value: metadata });
		} catch (error) {
			try {
				await this.bannerFilesAdapter.remove(metadata.id);
			} catch {
				// Ignore cleanup failures so the original storage error is preserved.
			}

			if (this.isQuotaExceededError({ error })) {
				throw new StorageQuotaExceededError({ requiredBytes: file.size });
			}

			throw error;
		}

		return metadata;
	}

	async loadBanners(): Promise<Array<{ metadata: BannerMetadata; file: File }>> {
		const ids = await this.bannersAdapter.list();
		const items: Array<{ metadata: BannerMetadata; file: File }> = [];

		for (const id of ids) {
			const [metadata, file] = await Promise.all([
				this.bannersAdapter.get(id),
				this.bannerFilesAdapter.get(id),
			]);
			if (metadata && file) {
				items.push({ metadata, file });
			}
		}

		return items;
	}

	async removeBanner({ id }: { id: string }): Promise<void> {
		await Promise.all([
			this.bannersAdapter.remove(id),
			this.bannerFilesAdapter.remove(id),
		]);
	}

	/** Overwrites just the remembered position/scale/rotation for one banner — called whenever a
	 * banner-sourced element's transform changes on any timeline (see
	 * banners/banner-position-tracker.ts). Leaves the file/other metadata untouched. */
	async updateBannerTransform({
		id,
		transform,
	}: {
		id: string;
		transform: BannerTransform;
	}): Promise<void> {
		const existing = await this.bannersAdapter.get(id);
		if (!existing) return;
		await this.bannersAdapter.set({
			key: id,
			value: { ...existing, lastTransform: transform },
		});
	}

	/** `loopFile` is optional — a preset can be background+mask only, with no loop layer. */
	async savePreset({
		name,
		background,
		mask,
		loopFile,
		loopMediaType,
		loopWidth,
		loopHeight,
		loopDuration,
	}: {
		name: string;
		background?: TBackground;
		mask?: Mask;
		loopFile?: File;
		loopMediaType?: "image" | "video";
		loopWidth?: number;
		loopHeight?: number;
		loopDuration?: number;
	}): Promise<LookPresetMetadata> {
		const metadata: LookPresetMetadata = {
			id: generateUUID(),
			name,
			createdAt: new Date().toISOString(),
			background,
			mask,
			loop:
				loopFile && loopMediaType
					? {
							mediaType: loopMediaType,
							type: loopFile.type,
							size: loopFile.size,
							width: loopWidth,
							height: loopHeight,
							duration: loopDuration,
						}
					: undefined,
		};

		try {
			if (loopFile) {
				await this.presetLoopFilesAdapter.set({ key: metadata.id, value: loopFile });
			}
			await this.presetsAdapter.set({ key: metadata.id, value: metadata });
		} catch (error) {
			if (loopFile) {
				try {
					await this.presetLoopFilesAdapter.remove(metadata.id);
				} catch {
					// Ignore cleanup failures so the original storage error is preserved.
				}
			}

			if (loopFile && this.isQuotaExceededError({ error })) {
				throw new StorageQuotaExceededError({ requiredBytes: loopFile.size });
			}

			throw error;
		}

		return metadata;
	}

	async loadPresets(): Promise<Array<{ metadata: LookPresetMetadata; loopFile: File | null }>> {
		const ids = await this.presetsAdapter.list();
		const items: Array<{ metadata: LookPresetMetadata; loopFile: File | null }> = [];

		for (const id of ids) {
			const [metadata, loopFile] = await Promise.all([
				this.presetsAdapter.get(id),
				this.presetLoopFilesAdapter.get(id),
			]);
			if (metadata) {
				items.push({ metadata, loopFile: loopFile ?? null });
			}
		}

		return items;
	}

	async removePreset({ id }: { id: string }): Promise<void> {
		await Promise.all([
			this.presetsAdapter.remove(id),
			this.presetLoopFilesAdapter.remove(id),
		]);
	}

	async loadSavedSounds(): Promise<SavedSoundsData> {
		try {
			const savedSoundsData = await this.savedSoundsAdapter.get("user-sounds");
			return (
				savedSoundsData || {
					sounds: [],
					lastModified: new Date().toISOString(),
				}
			);
		} catch (error) {
			console.error("Failed to load saved sounds:", error);
			return { sounds: [], lastModified: new Date().toISOString() };
		}
	}

	async saveSoundEffect({
		soundEffect,
	}: {
		soundEffect: SoundEffect;
	}): Promise<void> {
		try {
			const currentData = await this.loadSavedSounds();

			if (currentData.sounds.some((sound) => sound.id === soundEffect.id)) {
				return; // Already saved
			}

			const savedSound: SavedSound = {
				id: soundEffect.id,
				name: soundEffect.name,
				username: soundEffect.username,
				previewUrl: soundEffect.previewUrl,
				downloadUrl: soundEffect.downloadUrl,
				duration: soundEffect.duration,
				tags: soundEffect.tags,
				license: soundEffect.license,
				savedAt: new Date().toISOString(),
			};

			const updatedData: SavedSoundsData = {
				sounds: [...currentData.sounds, savedSound],
				lastModified: new Date().toISOString(),
			};

			await this.savedSoundsAdapter.set({
				key: "user-sounds",
				value: updatedData,
			});
		} catch (error) {
			console.error("Failed to save sound effect:", error);
			throw error;
		}
	}

	async removeSavedSound({ soundId }: { soundId: number }): Promise<void> {
		try {
			const currentData = await this.loadSavedSounds();

			const updatedData: SavedSoundsData = {
				sounds: currentData.sounds.filter((sound) => sound.id !== soundId),
				lastModified: new Date().toISOString(),
			};

			await this.savedSoundsAdapter.set({
				key: "user-sounds",
				value: updatedData,
			});
		} catch (error) {
			console.error("Failed to remove saved sound:", error);
			throw error;
		}
	}

	async isSoundSaved({ soundId }: { soundId: number }): Promise<boolean> {
		try {
			const currentData = await this.loadSavedSounds();
			return currentData.sounds.some((sound) => sound.id === soundId);
		} catch (error) {
			console.error("Failed to check if sound is saved:", error);
			return false;
		}
	}

	async clearSavedSounds(): Promise<void> {
		try {
			await this.savedSoundsAdapter.remove("user-sounds");
		} catch (error) {
			console.error("Failed to clear saved sounds:", error);
			throw error;
		}
	}

	isOPFSSupported(): boolean {
		return OPFSAdapter.isSupported();
	}

	isIndexedDBSupported(): boolean {
		return "indexedDB" in window;
	}

	isFullySupported(): boolean {
		return this.isIndexedDBSupported() && this.isOPFSSupported();
	}
}

export const storageService = new StorageService();
export { StorageService };
